import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { XCircle, RefreshCw, ArrowLeft, AlertTriangle } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { ticketsApi } from '../lib/api';
import api from '../lib/api';

const CheckoutFailurePage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get('order_id');
  const paymentId = searchParams.get('payment_id');
  const errorMessage = searchParams.get('error') || searchParams.get('message');
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrder = async () => {
      if (orderId) {
        try {
          // First, ask the backend to sync the order status from MP. This
          // moves the order out of "pending" if the user actually came here
          // because the payment was rejected. The webhook is source of truth
          // for approvals, this is only for terminal failure states.
          try {
            await api.post(`/tickets/orders/${orderId}/mark-from-mp`, null, {
              params: paymentId ? { payment_id: paymentId } : undefined,
            });
          } catch (_) { /* non-fatal */ }
          const response = await ticketsApi.getOrder(orderId);
          setOrder(response.data);
        } catch (error) {
          console.error('Error fetching order:', error);
        }
      }
      setLoading(false);
    };
    fetchOrder();
  }, [orderId, paymentId]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Navbar />
      
      <section className="pt-8 md:pt-32 pb-24 px-6 lg:px-12" data-testid="checkout-failure-page">
        <div className="max-w-2xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            {/* Icon */}
            <motion.div 
              className="w-24 h-24 mx-auto mb-8 rounded-full bg-red-500/10 flex items-center justify-center"
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            >
              <XCircle size={48} className="text-red-500" />
            </motion.div>
            
            {/* Title */}
            <h1 className="text-4xl lg:text-5xl font-black tracking-tighter uppercase mb-4">
              Pago Rechazado
            </h1>
            
            <p className="text-[#A1A1AA] text-lg mb-8 max-w-md mx-auto">
              No pudimos procesar tu pago. Por favor, intentá nuevamente o usá otro medio de pago.
            </p>

            {/* Error Details */}
            {errorMessage && (
              <motion.div 
                className="bg-red-500/10 border border-red-500/30 p-4 mb-8 text-left max-w-md mx-auto"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="text-red-500 flex-shrink-0 mt-0.5" size={20} />
                  <div>
                    <p className="text-red-400 font-medium text-sm">Motivo del rechazo:</p>
                    <p className="text-red-300/80 text-sm mt-1">{errorMessage}</p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Order Details */}
            {!loading && order && (
              <motion.div 
                className="bg-[#121212] border border-[#27272A] p-8 text-left mb-8"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
              >
                <h2 className="text-xl font-black uppercase tracking-tight mb-6">
                  Detalles de la Orden
                </h2>
                
                <div className="space-y-4">
                  {order.event_title && (
                    <div className="flex justify-between border-b border-[#27272A] pb-4">
                      <span className="text-[#A1A1AA]">Evento</span>
                      <span className="font-medium">{order.event_title}</span>
                    </div>
                  )}
                  <div className="flex justify-between border-b border-[#27272A] pb-4">
                    <span className="text-[#A1A1AA]">Cantidad de entradas</span>
                    <span className="font-mono">{order.quantity}</span>
                  </div>
                  <div className="flex justify-between border-b border-[#27272A] pb-4">
                    <span className="text-[#A1A1AA]">Total</span>
                    <span className="font-mono font-bold">${order.total_amount?.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-[#A1A1AA]">Estado</span>
                    <span className="font-mono uppercase text-red-500">
                      Rechazado
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Tips for retry */}
            <motion.div 
              className="bg-[#121212] border border-[#27272A] p-6 mb-8 text-left max-w-md mx-auto"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <h3 className="font-bold text-white mb-3">Sugerencias:</h3>
              <ul className="text-[#A1A1AA] text-sm space-y-2">
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Verificá que los datos de tu tarjeta sean correctos
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Asegurate de tener fondos suficientes
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Probá con otra tarjeta o medio de pago
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-[#D4AF37]">•</span>
                  Si el problema persiste, contactá a tu banco
                </li>
              </ul>
            </motion.div>

            {/* Action Buttons */}
            <motion.div 
              className="flex flex-col sm:flex-row gap-4 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
            >
              {order && (
                <Link 
                  to={`/checkout/${order.event_id}`} 
                  className="btn-primary inline-flex items-center justify-center group"
                  data-testid="retry-payment-btn"
                >
                  <RefreshCw size={18} className="mr-2" />
                  Intentar de Nuevo
                </Link>
              )}
              <Link 
                to="/eventos" 
                className="btn-secondary inline-flex items-center justify-center"
                data-testid="back-to-events-btn"
              >
                <ArrowLeft size={18} className="mr-2" />
                Volver a Eventos
              </Link>
            </motion.div>

            {/* Support */}
            <motion.p 
              className="text-[#52525B] text-sm mt-8"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6 }}
            >
              ¿Necesitás ayuda? Contactanos a{' '}
              <a href="mailto:central@bravoproductora.com" className="text-[#D4AF37] hover:underline">
                central@bravoproductora.com
              </a>
            </motion.p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default CheckoutFailurePage;
